import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { createIdentityAdmin, fetchIdentityAdmin, fetchIdentityDashboard } from '../../services/identityService';
import AdvancedConfiguration from '../super-admin/components/AdvancedConfiguration';
import AdminRecordTable from '../super-admin/components/AdminRecordTable';
import PageHeader from '../super-admin/components/PageHeader';
import WorkspaceNav from '../super-admin/components/WorkspaceNav';
import { safeAdminError } from '../super-admin/components/adminPresentation';

export const IDENTITY_ADMIN_SECTIONS = [
  ['dashboard', 'Dashboard'], ['nodes', 'Identity Management'], ['relationshipDefinitions', 'Relationship Rules'], ['relationships', 'Identity Graph'],
  ['verificationDefinitions', 'Verification Rules'], ['verifications', 'Verification'], ['badgeDefinitions', 'Badge Rules'], ['badges', 'Badges'],
  ['achievementDefinitions', 'Achievement Rules'], ['achievementProgress', 'Achievements'], ['reputationDefinitions', 'Reputation Rules'], ['reputation', 'Reputation'],
  ['reviews', 'Review Moderation'], ['mergeRequests', 'Identity Merge'], ['duplicates', 'Duplicate Detection'], ['featured', 'Featured Profiles'],
  ['activities', 'Public Activity'], ['analytics', 'Analytics'], ['configuration', 'Platform Configuration'],
];
const IDENTITY_GROUPS = [
  { label: 'Overview', items: IDENTITY_ADMIN_SECTIONS.filter(([key]) => ['dashboard', 'analytics'].includes(key)) },
  { label: 'Profiles', items: IDENTITY_ADMIN_SECTIONS.filter(([key]) => ['nodes', 'relationships', 'relationshipDefinitions', 'featured', 'activities'].includes(key)) },
  { label: 'Verification & moderation', items: IDENTITY_ADMIN_SECTIONS.filter(([key]) => ['verificationDefinitions', 'verifications', 'reviews', 'mergeRequests', 'duplicates'].includes(key)) },
  { label: 'Recognition', items: IDENTITY_ADMIN_SECTIONS.filter(([key]) => ['badgeDefinitions', 'badges', 'achievementDefinitions', 'achievementProgress', 'reputationDefinitions', 'reputation'].includes(key)) },
  { label: 'Configure', items: IDENTITY_ADMIN_SECTIONS.filter(([key]) => key === 'configuration') },
];
const READ_ONLY = new Set(['dashboard', 'achievementProgress', 'reputation', 'activities', 'analytics']);

const IdentityAdminPage = () => {
  const { token } = useAuth();
  const [section, setSection] = useState('dashboard');
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState('{}');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const title = useMemo(() => IDENTITY_ADMIN_SECTIONS.find(([key]) => key === section)?.[1], [section]);
  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const payload = section === 'dashboard' ? await fetchIdentityDashboard(token) : await fetchIdentityAdmin(section, token);
      setItems(section === 'dashboard' ? [payload] : payload.items || []);
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load identity records.'));
    } finally {
      setBusy(false);
    }
  }, [section, token]);
  useEffect(() => { if (token) load(); }, [token, load]);
  const create = async () => {
    if (!reason.trim()) return setError('A change reason is required.');
    setBusy(true);
    setError('');
    try {
      await createIdentityAdmin(section, JSON.parse(draft), token, reason.trim());
      setDraft('{}');
      setReason('');
      await load();
    } catch (err) {
      setError(safeAdminError(err, 'Unable to save this identity record.'));
      setBusy(false);
    }
  };
  return (
    <section className="sa-workspace">
      <PageHeader title="Cricket Identity" description="Manage identity records, verification, badges, reputation, moderation, and discovery." />
      <WorkspaceNav groups={IDENTITY_GROUPS} value={section} onChange={setSection} ariaLabel="Cricket Identity sections" />
      {error ? <div className="sa-error-state" role="alert"><strong>Identity data could not be loaded.</strong><span>{error}</span></div> : null}
      <section className="sa-panel" role="tabpanel">
        <header className="sa-panel__header"><div><h2>{title}</h2><p>Canonical identity configuration and moderation records.</p></div><button className="sa-secondary-button" type="button" onClick={load} disabled={busy}>Refresh</button></header>
        {busy ? <div className="sa-table-skeleton" aria-label="Loading identity records" /> : <AdminRecordTable items={items} />}
      </section>
      {!READ_ONLY.has(section) ? <AdvancedConfiguration title="Advanced identity configuration" description="For reviewed identity definitions and administrative records only." draft={draft} setDraft={setDraft} reason={reason} setReason={setReason} busy={busy} onCreate={create} textareaLabel="Identity record JSON" /> : null}
    </section>
  );
};

export default IdentityAdminPage;
